import type { EntityId } from '@signalclash/shared';

export type SelectionRef =
  | { kind: 'squad'; id: EntityId }
  | { kind: 'core'; id: EntityId }
  | { kind: 'building'; id: EntityId };

export type SelectionListener = (current: SelectionRef | null) => void;

// Pure client-side selection. The simulation does not care which entity the user
// has highlighted — selection is a render/UI concern only. V0.4 supports squads,
// own Cores, and own Buildings (so the player can produce squads from a Hub).
export class Selection {
  private current: SelectionRef | null = null;
  private listeners = new Set<SelectionListener>();
  // Multi-select: extra squad ids selected alongside the primary. The
  // primary remains addressable by `getCurrent()` for HUD ability
  // dispatch; orders (move / attack-move) operate on every id in the
  // combined group. Group is cleared whenever a non-squad selection
  // happens or `clear()` is called.
  private additionalSquadIds: Set<EntityId> = new Set();

  selectSquad(id: EntityId): void {
    const next: SelectionRef = { kind: 'squad', id };
    this.additionalSquadIds.clear();
    if (this.same(this.current, next)) return;
    this.current = next;
    this.notify();
  }

  // Multi-select: set the primary to the first id (typically nearest to
  // cursor), keep the rest as additionals. No-op for empty arrays.
  selectSquadGroup(ids: EntityId[]): void {
    if (ids.length === 0) return;
    const primary = ids[0]!;
    this.additionalSquadIds = new Set(ids.slice(1));
    const next: SelectionRef = { kind: 'squad', id: primary };
    this.current = next;
    this.notify();
  }

  selectCore(id: EntityId): void {
    const next: SelectionRef = { kind: 'core', id };
    this.additionalSquadIds.clear();
    if (this.same(this.current, next)) return;
    this.current = next;
    this.notify();
  }

  selectBuilding(id: EntityId): void {
    const next: SelectionRef = { kind: 'building', id };
    this.additionalSquadIds.clear();
    if (this.same(this.current, next)) return;
    this.current = next;
    this.notify();
  }

  clear(): void {
    if (this.current === null && this.additionalSquadIds.size === 0) return;
    this.current = null;
    this.additionalSquadIds.clear();
    this.notify();
  }

  getCurrent(): SelectionRef | null {
    return this.current;
  }

  isSquadSelected(id: EntityId): boolean {
    if (this.current !== null && this.current.kind === 'squad' && this.current.id === id) return true;
    return this.additionalSquadIds.has(id);
  }

  isCoreSelected(id: EntityId): boolean {
    return this.current !== null && this.current.kind === 'core' && this.current.id === id;
  }

  isBuildingSelected(id: EntityId): boolean {
    return this.current !== null && this.current.kind === 'building' && this.current.id === id;
  }

  // Compatibility: return the currently-selected squad as an array; empty otherwise.
  // Used by InputRouter.handleRightClick which dispatches MoveCommand for selected squads.
  // Returns the primary id first, then any additional group ids — order matches
  // the squad-group selectGroup() call so callers can rely on [0] being the
  // primary for ability dispatch.
  selectedSquadIds(): EntityId[] {
    if (this.current === null || this.current.kind !== 'squad') return [];
    if (this.additionalSquadIds.size === 0) return [this.current.id];
    return [this.current.id, ...this.additionalSquadIds];
  }

  subscribe(listener: SelectionListener): () => void {
    this.listeners.add(listener);
    listener(this.current);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private same(a: SelectionRef | null, b: SelectionRef | null): boolean {
    if (a === null || b === null) return a === b;
    return a.kind === b.kind && a.id === b.id;
  }

  private notify(): void {
    // Containment: a Sfx playSelection throw (audio context lost mid-click)
    // must not stop the other Selection subscribers from refreshing the
    // HUD / minimap / build panel. The notify fires on every click —
    // any single-handler failure would otherwise freeze the visible
    // selection state until the next clean click.
    for (const fn of this.listeners) {
      try {
        fn(this.current);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[selection listener] threw', err);
      }
    }
  }
}
